import { ClearinghouseTransactionType } from '../../api';
import { IRoutableConnection } from './payer-lanes';

/**
 * Which clearinghouse connection carries each EDI transaction.
 *
 * This is the drawer's TRANSACTION / SERVED BY table, and it is **not** the nine lanes.
 * The lanes are kinds of data that move between us and a payer; these are the nine X12
 * transactions a clearinghouse can be configured for. They differ deliberately in both
 * directions: 834 appears here and has no lane, because benefit enrolment is not claim
 * correspondence; and the three paper-by-definition lanes appear there with no transaction
 * here, because X12 has nothing that carries an appeal. Confusing the two would be a defect
 * (NS-6, NS-7).
 *
 * There is no priority between connections. When a payer has more than one, they are
 * complementary rather than ranked — typically one carries EDI_275 attachments and the other
 * carries everything else, which is how a workers-comp payer needing paperwork ends up on a
 * different connection from one that does not. So "the connection that supports it" is a
 * complete answer, and at most one normally does.
 *
 * ## Why these take `IRoutableConnection` and not `SupportClearinghousePayer`
 *
 * They used to take the concrete support model, which is the only reason none of this was
 * reachable from the provider app — `SupportClearinghousePayer` and `ProviderClearinghousePayer`
 * are structurally identical here but nominally distinct, so the type was the whole blocker.
 * Every function below reads exactly one field, `transactions`, and every caller reads at most
 * `externalId`, `clearinghouse.name` and `payer.name`. `IRoutableConnection` declares all four.
 *
 * Widening a parameter cannot break an existing caller, so support keeps working unchanged;
 * this is the same move already made on `getPayerLanes`, for the same reason.
 */

export interface IPayerTransaction {
    transaction: ClearinghouseTransactionType;
    /** "Professional claims", "Remittance (ERA)" — the design's wording. */
    label: string;
    /** The connection carrying it, or undefined when nothing does. */
    servedBy?: IRoutableConnection;
}

/** The nine transactions, in the order the design's EDI grid and table list them. */
const TRANSACTIONS: Array<{ transaction: ClearinghouseTransactionType; label: string }> = [
    { transaction: ClearinghouseTransactionType.EDI837P, label: 'Professional claims' },
    { transaction: ClearinghouseTransactionType.EDI837I, label: 'Institutional claims' },
    { transaction: ClearinghouseTransactionType.EDI837D, label: 'Dental claims' },
    { transaction: ClearinghouseTransactionType.EDI270271, label: 'Eligibility checks' },
    { transaction: ClearinghouseTransactionType.EDI276277, label: 'Claim statuses' },
    { transaction: ClearinghouseTransactionType.EDI278, label: 'Authorizations' },
    { transaction: ClearinghouseTransactionType.EDI834, label: 'Benefit enrollment' },
    { transaction: ClearinghouseTransactionType.EDI835, label: 'Remittance (ERA)' },
    { transaction: ClearinghouseTransactionType.EDI275, label: 'Attachments (275)' },
];

export const TRANSACTION_COUNT = TRANSACTIONS.length;

/**
 * @param bindings the payer's clearinghouse connections, or `undefined` while the join is in
 *                 flight. Undefined yields every transaction unserved, which the caller must
 *                 render as unknown rather than "drops to paper" — not loaded is not the same
 *                 as no connection (NS-3).
 */
export function getTransactionCoverage(bindings: IRoutableConnection[] | undefined): IPayerTransaction[] {
    return TRANSACTIONS.map(({ transaction, label }) => ({
        transaction,
        label,
        servedBy: (bindings || []).find(binding => (binding.transactions || []).includes(transaction)),
    }));
}

/** "5 of 9 transactions covered" in the drawer's clearinghouse header. */
export function countCovered(coverage: IPayerTransaction[]): { covered: number; total: number } {
    return { covered: coverage.filter(t => !!t.servedBy).length, total: coverage.length };
}

/** How many of the nine a single connection carries — "TriZetto · 4 of 9 transactions". */
export function countBindingTransactions(binding: IRoutableConnection): { covered: number; total: number } {
    const supported = (binding.transactions || []).filter(t =>
        TRANSACTIONS.some(known => known.transaction === t),
    );

    return { covered: supported.length, total: TRANSACTIONS.length };
}

/** The design's per-connection chips: the transactions this connection actually carries. */
export function bindingTransactionLabels(binding: IRoutableConnection): string[] {
    return TRANSACTIONS.filter(t => (binding.transactions || []).includes(t.transaction)).map(t => t.label);
}
